
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { BaseDestination } from '@/components/destination/types/destination';

interface FavoriteDestination {
  id: string;
  type: string;
  destination: BaseDestination;
  notes?: string;
}

interface SavedDestinationsProps {
  favorites: FavoriteDestination[];
}

export const SavedDestinations = ({ favorites }: SavedDestinationsProps) => {
  // Filter to ensure we only show destinations with destination data
  const destinationFavorites = favorites.filter(f => 
    f.type === 'destination' && f.destination && f.destination.id
  );

  if (destinationFavorites.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <h3 className="text-lg font-medium mb-2">No saved destinations yet</h3>
        <p className="text-gray-500">
          Save destinations by clicking the heart icon when browsing places you like.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {destinationFavorites.map((favorite) => {
        // Create SEO-friendly URL slugs for each destination
        const countrySlug = favorite.destination.country.toLowerCase().replace(/\s+/g, '-');
        const nameSlug = favorite.destination.name.toLowerCase().replace(/\s+/g, '-');
        const destinationUrl = `/destinations/${countrySlug}/${nameSlug}/${favorite.destination.id}`;
        
        return (
          <Link to={destinationUrl} key={favorite.id}>
            <Card className="h-full hover:shadow-lg transition-shadow">
              {favorite.destination.image_url && (
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={favorite.destination.image_url}
                    alt={favorite.destination.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardHeader>
                <CardTitle>
                  <div>
                    <span className="text-xl">{favorite.destination.name}</span>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{favorite.destination.country}</span>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {favorite.destination.description}
                </p>
                {favorite.notes && (
                  <p className="mt-4 text-sm border-t pt-4 text-gray-600">
                    Note: {favorite.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
};
